import { DoubleIndex, SingleIndex, assert } from '@deqo/foundation';
import { ComponentSetOperation } from '@deqo/podopolis';
import { Component } from './Component';
import { ComponentType } from './Interfaces';

const Names = (left: string, right: string) => ({ left, right });
type Names = ReturnType<typeof Names>;

type ComponentId = string;

export class RelationManager {

    // Stores the definitions of the relations, e.g. type -> property -> names
    private definitions = new DoubleIndex<string, Names>();

    // Stores the left to right relations, e.g. id -> leftName -> Component[]
    private leftToRight = new DoubleIndex<string, ComponentId>();

    // Stores the right to left relations, e.g. id -> rightName -> Component[]
    private rightToLeft = new DoubleIndex<string, ComponentId>();

    // Stores which components have which relationships defined, e.g. id -> names
    private tracker = new SingleIndex<string, Names>();

    // List of ids that have been invalidated
    private invalidations = new Set<ComponentId>();

    constructor(private getComponentById: (id: string) => Component) { }

    public define<T extends Component, K extends keyof T>(type: ComponentType<T>, property: K | string, leftName: string, rightName) {
        assert(typeof property === 'string', 'RelationManager.define: property must be a string');
        const desc = Component.describe(type);
        this.definitions.add(desc.name, property, Names(leftName, rightName));
    }

    public undefine<T extends Component, K extends keyof T>(type: ComponentType<T>, property: K | string) {
        assert(typeof property === 'string', 'RelationManager.undefine: property must be a string');
        const desc = Component.describe(type);
        this.definitions.delete(desc.name, property);
    }

    public invalidate(id: string) {
        this.invalidations.add(id);
        this.remove(id);
    }

    public notifyComponentSet([_, id, ...kvps]: ComponentSetOperation) {
        // Skip if already invalidated
        if (this.invalidations.has(id)) return;

        // Find which props
        const comp = this.getComponentById(id);
        if (!!comp?.alive) {
            for (let i = 0; i < kvps.length; i += 2) {
                const k = kvps[i] as string;
                if (this.definitions.has(comp.type, k)) {
                    this.invalidate(comp.id);
                    break;
                }
            }
        }
        else {
            // If missing or dead
            this.invalidate(id);
        }
    }

    public query(id: string, relationName: string) {
        this.refreshInvalidated();
        return [
            ...Array.from(this.leftToRight.get(id, relationName)),
            ...Array.from(this.rightToLeft.get(id, relationName)),
        ];
    }

    private refreshInvalidated() {
        if (this.invalidations.size === 0) return;
        for (const id of this.invalidations) {
            this.remove(id);
            this.add(id);
        }
        this.invalidations.clear();

        // for (const [id, rels] of this.leftToRight) {
        //     for (const [name, otherSet] of rels) {
        //         for (const otherId of otherSet) {
        //             console.log(id, 'is', name, 'of', otherId);
        //         }
        //     }
        // }
        // for (const [id, rels] of this.rightToLeft) {
        //     for (const [name, otherSet] of rels) {
        //         for (const otherId of otherSet) {
        //             console.log(id, 'is', name, 'of', otherId);
        //         }
        //     }
        // }
    }

    private add(id: string) {

        const comp = this.getComponentById(id);
        if (!comp?.alive) return;
        const defs = this.definitions.getUpper(comp.type);

        for (const [property, nameSet] of defs) {
            for (const names of nameSet) {

                const value =
                    (property in comp ? comp[property] : null) ??
                    (comp.has(property) ? comp.get(property) : null) ??
                    null;

                if (!value) continue;

                const other = this.getComponentById(value);
                if (!other) continue;

                this.leftToRight.add(comp.id, names.left, other.id);
                this.rightToLeft.add(other.id, names.right, comp.id);

                this.tracker.add(comp.id, names);
                this.tracker.add(other.id, names);
            }
        }
    }

    private remove(id: string) {

        const nameSet = this.tracker.get(id);
        for (const names of nameSet) {

            const otherSet = this.leftToRight.get(id, names.left);

            for (const otherId of otherSet) {
                this.leftToRight.remove(id, names.left, otherId);
                this.rightToLeft.remove(otherId, names.right, id);
            }
        }
    }
}
